import { useContext } from 'react'
import { ProcessedMessage } from './useProcessMessage'
import useUser from '../useUser'
import { ModuleType, TimeSuggestionOptionEnum } from '../../context/GlobalContext'
import useModules from '../module/useModules'
import useRoles, { rolesWriteAllTimes } from '../useRoles'
import useQueueDispatch from '../queue/useQueueDispatch'
import { useQueue } from '../useQueue'
import { QueueJobKind, QueueStatus } from '../queue/queueTypes'
import { AxiosResponse } from 'axios'
import { useSettings } from '../useSettings'
import { mapOverviewItemToTrackTimeSuggestionDto, TimeSuggestionOverviewDto, TimeSuggestionStatus } from '../../pages/Time/BaseTimeSuggestionOverview'
import { useTranslation } from 'react-i18next'
import useAxios from '../useAxios'
import PopupContext from '../../context/PopupContext'
import { useTimeDivisorSetting } from '../file/useTimeDivisorSetting'

/**
 * Messages originating from the Inboxer in order to invoke a TimeSuggestionDialog.
 */
export type TimeTrackingNotification = ProcessedMessage<{
    fileNumber: string
    timeSuggestionId: string
    date: string
    duration: string
    sender: string
    recipients: string
    emailInbound: boolean
    subject: string
    isAutomatic: boolean
    isBatch: boolean
    references: string[]
}>

/**
 * Process a time-tracking event message. This invokes the TimeSuggestionDialog with custom initialValues.
 */
export default function useProcessTimeTrackingNotification() {
    const axios = useAxios()
    const modules = useModules()
    const popupContext = useContext(PopupContext)
    const roles = useRoles()
    const { settings } = useSettings()
    const timeDivisor = useTimeDivisorSetting()
    const { i18n } = useTranslation()
    const { currentUser } = useUser()

    const queue = useQueue()
    const dispatch = useQueueDispatch(QueueJobKind.TimeSuggestion)

    return (message: TimeTrackingNotification) => {
        if (!modules.has(ModuleType.TimeTracking) || !roles.any(rolesWriteAllTimes) || currentUser?.useTimePopup === false) {
            // Only push a time tracking popup to time tracking users that want a popup.
            return
        }

        // A failed suggestion arrives with a null id; interpolating it would request /time-suggestion/null.
        if (!message.data?.timeSuggestionId) {
            return
        }

        let notificationOption: TimeSuggestionOptionEnum
        if (message.data?.isAutomatic) {
            notificationOption = message.data?.emailInbound ? settings.timeSuggestion?.incomingEmail : settings.timeSuggestion?.sentEmail
        } else {
            notificationOption = message.data?.isBatch ? settings.timeSuggestion?.fileMultipleEmail : settings.timeSuggestion?.fileSingleEmail
        }
        if (notificationOption === undefined) {
            notificationOption = TimeSuggestionOptionEnum.Notification
        }

        if (notificationOption === TimeSuggestionOptionEnum.Popup) {
            axios
                .get<TimeSuggestionOverviewDto>(`/time-suggestion/${message.data.timeSuggestionId}`, {
                    headers: { SuppressErrors: 'true' },
                })
                .then(response => {
                    const timeSuggestion = response.data as TimeSuggestionOverviewDto
                    // If we already tracked time for this email, skip popup.
                    if (timeSuggestion.status !== TimeSuggestionStatus.ToReview) {
                        return
                    }

                    popupContext.setShowTimeSuggestionDialog(
                        mapOverviewItemToTrackTimeSuggestionDto(
                            timeSuggestion,
                            timeDivisor,
                            i18n.language,
                            settings.timeSuggestionDescription,
                            currentUser?.id,
                        ),
                    )
                })
        } else if (notificationOption === TimeSuggestionOptionEnum.Notification) {
            const requestId = message.data.timeSuggestionId
            dispatch(requestId, {
                baseURL: undefined,
                url: `/time-suggestion/${requestId}`,
                method: 'GET',
                mapDispatchResponse: (response: AxiosResponse<void>) => response.data,
                callbacks: {
                    // The suggestion is complete as soon as the server acknowledges it; there is no further
                    // notification to wait for.
                    [QueueStatus.Queued]: () => queue.transition(requestId, QueueStatus.Finished),
                },
            })
        } else {
            console.log('Notification option for time tracking of email is NONE.')
        }
    }
}
